import React from 'react';
import {
  Box,
  VStack,
  Heading,
  Text,
  Container,
  useColorModeValue,
  Flex,
  useBreakpointValue
} from '@chakra-ui/react';

import ms3Website from '../../assets/ms3Website.png';

const History = () => {
  const accentColor = useColorModeValue('yellow.400', 'yellow.500');
  const headingSize = useBreakpointValue({ base: 'xl', md: '2xl' });
  const subHeadingSize = useBreakpointValue({ base: 'md', md: 'lg' });

  return (
    <Box minH="100vh" position="relative" overflow="hidden" marginTop="16">
      <Container
        maxW="7xl"
        position="relative"
        zIndex="1"
        display="flex"
        alignItems="center"
        justifyContent="center"
        minH="80vh"
      >
        <VStack spacing={{ base: 12, md: 16 }} align="center" w="full">
          {/* Header Section */}
          <VStack spacing={{ base: 4, md: 6 }} textAlign="center" maxW="4xl">
            <Heading as="h2" size={headingSize} color="gray.800" fontWeight="bold" lineHeight="1.2">
              Our Journey
            </Heading>
            <Text fontSize={{ base: 'md', md: 'lg' }} color="gray.600" maxW="3xl" lineHeight="1.6" fontWeight="medium">
              Every great idea starts with a simple beginning. Here's where it all began...
            </Text>
          </VStack>

          {/* Story Section */}
          <Box
            maxW="5xl"
            w="full"
            position="relative"
            bg="white"
            borderRadius="3xl"
            boxShadow="2xl"
            overflow="hidden"
            border="1px solid"
            borderColor="gray.100"
            p={{ base: 8, md: 12 }}
          >
            <Flex
              direction={{ base: 'column', lg: 'row' }}
              align="center"
              gap={{ base: 8, lg: 12 }}
              position="relative"
              zIndex="2"
            >
              {/* Left side - Story */}
              <VStack flex="1" align={{ base: 'center', lg: 'flex-start' }} spacing={{ base: 6, md: 8 }} textAlign={{ base: 'center', lg: 'left' }}>
                <Heading as="h3" size={subHeadingSize} color="gray.700" fontWeight="semibold">
                  <Box as="span" position="relative">
                    It Started with Coffee
                    <Box
                      position="absolute"
                      bottom={-1}
                      left="0"
                      w="100%"
                      h="3px"
                      bg={accentColor}
                      borderRadius="full"
                      transform="rotate(-1deg)"
                      opacity={0.8}
                    />
                  </Box>
                </Heading>
                <Text fontSize={{ base: 'md', md: 'lg' }} color="gray.600" lineHeight="1.7" maxW="400px">
                  Our first attempt at restaurant digitization was a simple, static menu website
                  for <strong>MS3 beverages</strong> - a small beverage shop
                  specializing in sodas, coffee, and fresh juices.
                </Text>
                <Text fontSize={{ base: 'md', md: 'lg' }} color="gray.600" lineHeight="1.7" maxW="400px">
                  A simple, professional-looking website that would convey trust and legitimacy,
                  helping attract and retain customers in the digital age.
                </Text>
              </VStack>

              {/* Arrow - Desktop only */}
              <Box
                display={{ base: 'none', lg: 'flex' }}
                alignItems="center"
                justifyContent="center"
                position="relative"
                minW="140px"
              >
                <svg
                  width="140"
                  height="100"
                  viewBox="0 0 140 100"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ display: 'block' }}
                >
                  <defs>
                    <marker
                      id="arrowhead"
                      markerWidth="12"
                      markerHeight="8"
                      refX="10"
                      refY="4"
                      orient="auto"
                      markerUnits="strokeWidth"
                    >
                      <polygon points="0 0, 12 4, 0 8" fill="#D69E2E" />
                    </marker>
                  </defs>
                  <path
                    d="M10 70 Q70 10, 130 50"
                    stroke="#D69E2E"
                    strokeWidth="3"
                    fill="none"
                    strokeLinecap="round"
                    markerEnd="url(#arrowhead)"
                  />
                </svg>
              </Box>

              {/* mobile arrow */}
              <Box
                display={{ base: 'flex', lg: 'none' }}
                alignItems="center"
                justifyContent="center"
                py={4}
              >
                <Text
                  fontSize="5xl" // Bigger size
                  fontWeight="bold" // Stronger weight
                  color={accentColor}
                  lineHeight="1"
                >
                  ↓
                </Text>
              </Box>


              {/* Right side - Website Preview */}
              <VStack flex="1" spacing={6} align="center" maxW={{ base: '100%', lg: '400px' }} >
                <Box
                  position="relative"
                  w="100%"
                  maxW="320px"
                  bg="gray.900"
                  borderRadius="2xl"
                  p={3}
                  boxShadow="2xl"
                  transform={{ base: 'none', md: 'rotate(5deg)' }}
                  _hover={{ transform: { base: 'none', md: 'rotate(0deg) scale(1.02)' } }}
                  transition="all 0.3s ease"

                >
                  {/* Browser bar */}
                  <Box bg="gray.700" borderRadius="lg" p={2} mb={2}>
                    <Flex align="center" gap={2}>
                      <Box w="8px" h="8px" bg="red.400" borderRadius="full" />
                      <Box w="8px" h="8px" bg="yellow.400" borderRadius="full" />
                      <Box w="8px" h="8px" bg="green.400" borderRadius="full" />
                      <Box flex="1" bg="gray.600" borderRadius="md" px={2} py={1} ml={4}>
                        <Text fontSize="xs" color="gray.300">
                          msthree.com
                        </Text>
                      </Box>
                    </Flex>
                  </Box>

                  {/* Website screenshot (real image, not simulated) */}
                  <Box
                    borderRadius="lg"
                    overflow="hidden"
                    minH="150px"
                  >
                    <img
                      src={ms3Website}
                      alt="MS3 Website"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block'
                      }}

                    />
                  </Box>
                </Box>

                <VStack spacing={3} align="center">
                  <Box
                    as="a"
                    href="https://msthree.onrender.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    bg={accentColor}
                    color="gray.800"
                    px={6}
                    py={3}
                    borderRadius="full"
                    fontSize="sm"
                    fontWeight="semibold"
                    textDecoration="none"
                    boxShadow="lg"
                    _hover={{ transform: 'translateY(-2px)', boxShadow: 'xl', bg: 'yellow.500' }}
                    transition="all 0.3s ease"
                  >
                    Visit the Original Site →
                  </Box>
                </VStack>
              </VStack>
            </Flex>
          </Box>

          {/* Bottom text */}
          <VStack spacing={4} textAlign="center" maxW="3xl">
            <Text
              fontSize={{ base: 'md', md: 'lg' }}
              color="gray.600"
              lineHeight="1.6"
              fontStyle="italic"
            >
              The Website is still being used by over 300 customers every month.          
            </Text>

            <Box
              w="60px"
              h="2px"
              bg={accentColor}
              borderRadius="full"
              opacity="0.8"
            />
          </VStack>
        </VStack>
      </Container>
    </Box>
  );
};

export default History;